import axios from 'axios';
import { isInLoginGracePeriod } from './api';

const propertyAPI = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token
propertyAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para lidar com erros
propertyAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Não invalidar token se estivermos no período de graça após login
      if (isInLoginGracePeriod()) {
        console.warn('[PropertyAPI] 401 durante período de graça após login, ignorando invalidação de token');
        return Promise.reject(error);
      }
      
      const isLoginPage = window.location.pathname === '/login';
      if (!isLoginPage) {
        const currentToken = localStorage.getItem('token');
        if (currentToken) {
          localStorage.removeItem('token');
          localStorage.removeItem('default_company_id');
          window.dispatchEvent(new CustomEvent('auth:token-invalid'));
        }
      }
    }
    return Promise.reject(error);
  }
);

export interface Property {
  id: string;
  name: string;
  address?: string;
  client_id: string;
}

export const searchProperties = async (query: string): Promise<Property[]> => {
  const response = await propertyAPI.get('/properties/search', {
    params: { query }
  });
  return response.data.properties || [];
};

export const getPropertiesByClient = async (clientId: string): Promise<Property[]> => {
  const response = await propertyAPI.get('/properties/client', {
    params: { client_id: clientId }
  });
  return response.data.properties || [];
};

export const createProperty = async (data: {
  client_id: string;
  name: string;
  address: string;
  zip_code?: string;
  city?: string;
  state?: string;
  property_type?: 'residential' | 'commercial' | 'other';
  price: number;
  special_instructions?: string;
}) => {
  const response = await propertyAPI.post('/properties', data);
  return response.data;
};

// Atualizar propriedade
export const updateProperty = async (propertyId: string, data: {
  name?: string;
  address?: string;
  zip_code?: string;
  city?: string;
  state?: string;
  property_type?: 'residential' | 'commercial' | 'other';
  price?: number;
  special_instructions?: string;
}) => {
  const response = await propertyAPI.put(`/properties/${propertyId}`, data);
  return response.data;
};

// Histórico de Instruções Especiais
export interface SpecialInstructionHistory {
  id: string;
  property_id: string;
  instructions: string;
  changed_by: string;
  changed_at: string;
  created_at: string;
  updated_at: string;
}

// Buscar instruções para funcionários
export const getInstructionsForEmployee = async (propertyId: string) => {
  const response = await propertyAPI.get(`/properties/${propertyId}/instructions`);
  return response.data;
};

// Buscar histórico de instruções
export const getInstructionHistory = async (propertyId: string): Promise<SpecialInstructionHistory[]> => {
  const response = await propertyAPI.get(`/properties/${propertyId}/instructions/history`);
  return response.data.history || [];
};

// Criar novo registro no histórico
export const createInstructionHistory = async (propertyId: string, data: {
  instructions: string;
  changed_by: string;
}) => {
  const response = await propertyAPI.post(`/properties/${propertyId}/instructions/history`, data);
  return response.data;
};

export default propertyAPI;
